/**
 * app-search — tipos + validador del search-state global del layout `_app`.
 *
 * Centraliza en la URL todos los overlays montados a nivel `_app` (command
 * palette y los diálogos globales de `AppLayoutDialogs`), de modo que pegar la
 * URL en otro navegador reabre exactamente la misma pantalla/diálogo.
 *
 * Patrón (igual que #9 `/?q=`): `validateSearch` omite defaults (no contamina la
 * URL en estado vacío) y `AppLayoutContent` refleja URL→signals + navega con
 * `{ replace: true }` al abrir/cerrar (idempotente, sobrevive a back/forward y
 * a una URL pegada).
 */

/** Identidad del diálogo global abierto (uno a la vez). */
export type AppDialogKind =
  | "choice"
  | "list-new"
  | "list-edit"
  | "group-new"
  | "group-edit"
  | "share";

/** Modal sin entidad (referencia de atajos). */
export type AppModalKind = "shortcuts";

export interface AppLayoutSearch {
  // ── Command palette ────────────────────────────────────────────────────────
  /** Palette abierto. */
  palette?: true;
  /** Query escrita en el palette (copiable). */
  pq?: string;
  /** Filtro del palette (all = default omitido). */
  pf?: "todo" | "list";

  // ── Diálogos globales (AppLayoutDialogs) ────────────────────────────────────
  /** Diálogo global abierto. */
  dialog?: AppDialogKind;
  /** Id de lista para `list-edit` / `share`. */
  listId?: string;
  /** Id de grupo para `group-edit`. */
  groupId?: string;
  /** Modal sin entidad abierto. */
  modal?: AppModalKind;

  // ── Detalle de tarea (drawer) — sub-estados ─────────────────────────────────
  /** Panel de schedule (fecha/recordatorio/recurrencia) abierto en el detalle. */
  sched?: true;

  // ── Vista de lista de tareas — estado de vista ──────────────────────────────
  /** Sección "Completadas" expandida (default colapsada → omitido). */
  done?: true;

  // ── Cambio de cuenta (multi-cuenta #18) ─────────────────────────────────────
  /** Switcher de cuentas abierto (`?account=switch`). */
  account?: "switch";
}

const DIALOG_KINDS = new Set<AppDialogKind>([
  "choice",
  "list-new",
  "list-edit",
  "group-new",
  "group-edit",
  "share",
]);

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() !== "" ? v : undefined;
}

// Type-guard: estrecha un string a AppDialogKind sin aserción insegura. El cast a
// ReadonlySet<string> solo ENSANCHA el tipo del Set (seguro), no estrecha el valor.
function isDialogKind(v: string): v is AppDialogKind {
  return (DIALOG_KINDS as ReadonlySet<string>).has(v);
}

/**
 * Valida + normaliza el search del layout. Omite defaults para mantener la URL
 * limpia. Tolerante: valores desconocidos se descartan (no rompe el render).
 */
export function validateAppLayoutSearch(search: Record<string, unknown>): AppLayoutSearch {
  const out: AppLayoutSearch = {};

  // ── Palette ──
  if (search.palette === true || search.palette === "1" || search.palette === "true") {
    out.palette = true;
  }
  const pq = str(search.pq);
  if (pq) {
    out.pq = pq;
    // Una query implica palette abierto aunque falte el flag (URL más corta).
    out.palette = true;
  }
  if (search.pf === "todo" || search.pf === "list") {
    out.pf = search.pf;
    out.palette = true;
  }

  // ── Diálogos globales ──
  const dialog = str(search.dialog);
  if (dialog && isDialogKind(dialog)) {
    out.dialog = dialog;
    const listId = str(search.listId);
    const groupId = str(search.groupId);
    if (listId) {
      out.listId = listId;
    }
    if (groupId) {
      out.groupId = groupId;
    }
  }

  // ── Modal sin entidad ──
  if (search.modal === "shortcuts") {
    out.modal = "shortcuts";
  }

  // ── Detalle: panel de schedule ──
  if (search.sched === true || search.sched === "1" || search.sched === "true") {
    out.sched = true;
  }

  // ── Vista de lista: completadas expandidas ──
  if (search.done === true || search.done === "1" || search.done === "true") {
    out.done = true;
  }

  // ── Switcher de cuentas (multi-cuenta) ──
  if (search.account === "switch") {
    out.account = "switch";
  }

  return out;
}
